import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Table,
  Form,
  Spinner,
  Alert,
  InputGroup,
  Card,
} from 'react-bootstrap';
import {
  FaSearch,
  FaArrowLeft,
  FaFileInvoice,
  FaCalendarAlt,
  FaRupeeSign,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const BalanceSheet = () => {
  const navigate = useNavigate();
  const [asOnDate, setAsOnDate] = useState('');
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  const fetchBalanceSheet = async () => {
    if (!asOnDate) {
      setAlert({
        show: true,
        message: 'Please select a date.',
        variant: 'warning',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}balance-sheet`,
        {
          as_on_date: asOnDate,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setBalanceSheet(response.data.balance_sheet);
      }
    } catch (err) {
      console.error('Balance Sheet Error:', err);
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to fetch balance sheet. Please try again.',
        variant: 'danger',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
    }
    setLoading(false);
  };

  // Calculate totals
  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + parseFloat(item.balance || 0), 0);
  };

  const totals = balanceSheet
    ? {
        assets: calculateTotal(balanceSheet.assets || []),
        liabilities: calculateTotal(balanceSheet.liabilities || []),
        equity: calculateTotal(balanceSheet.equity || []),
      }
    : null;

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaFileInvoice style={{ fontSize: '1.8rem' }} />
                Balance Sheet
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                View balance sheet as on a specific date
              </p>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate(-1)}
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaArrowLeft /> Back
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filter Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                As On Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={asOnDate}
                  onChange={(e) => setAsOnDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                    height: '42px',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-6">
              <Form.Label style={{ fontWeight: '600', color: 'transparent' }}>
                Action
              </Form.Label>
              <Button
                onClick={fetchBalanceSheet}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  height: '42px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch /> Get Balance Sheet
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Balance Sheet Report */}
        {balanceSheet && (
          <div className="row g-4">
            {/* Assets Card */}
            <div className="col-12 col-lg-6">
              <Card
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '30px' }}>
                  <h5
                    style={{
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: '#495057',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    Assets
                  </h5>
                  <Table hover style={{ marginBottom: '0' }}>
                    <thead
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6',
                      }}
                    >
                      <tr>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                          }}
                        >
                          Account
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                            textAlign: 'right',
                          }}
                        >
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceSheet.assets.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              textAlign: 'right',
                              color: '#28a745',
                              fontWeight: '500',
                            }}
                          >
                            <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                            {parseFloat(item.balance).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))}
                      {/* Total Row */}
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderTop: '2px solid #dee2e6',
                          fontWeight: '700',
                        }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.95rem',
                            fontWeight: '700',
                          }}
                        >
                          Total Assets
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.95rem',
                            textAlign: 'right',
                            color: '#28a745',
                            fontWeight: '700',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {totals.assets.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </div>

            {/* Liabilities & Equity Card */}
            <div className="col-12 col-lg-6">
              <Card
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '30px' }}>
                  {/* Liabilities Section */}
                  <h5
                    style={{
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: '#495057',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    Liabilities
                  </h5>
                  <Table hover style={{ marginBottom: '30px' }}>
                    <thead
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6',
                      }}
                    >
                      <tr>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                          }}
                        >
                          Account
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                            textAlign: 'right',
                          }}
                        >
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceSheet.liabilities.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              textAlign: 'right',
                              color: '#dc3545',
                              fontWeight: '500',
                            }}
                          >
                            <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                            {parseFloat(item.balance).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))}
                      {/* Subtotal Row */}
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderTop: '2px solid #dee2e6',
                          fontWeight: '600',
                        }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                          }}
                        >
                          Total Liabilities
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#dc3545',
                            fontWeight: '600',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {totals.liabilities.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {/* Equity Section */}
                  <h5
                    style={{
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: '#495057',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    Equity
                  </h5>
                  <Table hover style={{ marginBottom: '0' }}>
                    <thead
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6',
                      }}
                    >
                      <tr>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                          }}
                        >
                          Account
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            fontWeight: '600',
                            color: '#495057',
                            textAlign: 'right',
                          }}
                        >
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceSheet.equity.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontSize: '0.9rem',
                              textAlign: 'right',
                              color: '#667eea',
                              fontWeight: '500',
                            }}
                          >
                            <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                            {parseFloat(item.balance).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))}
                      {/* Subtotal Row */}
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderTop: '2px solid #dee2e6',
                          fontWeight: '600',
                        }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                          }}
                        >
                          Total Equity
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#667eea',
                            fontWeight: '600',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {totals.equity.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                      {/* Total Liabilities + Equity Row */}
                      <tr
                        style={{
                          backgroundColor: '#667eea',
                          color: 'white',
                          borderTop: '2px solid #dee2e6',
                          fontWeight: '700',
                        }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.95rem',
                            fontWeight: '700',
                          }}
                        >
                          Total Liabilities & Equity
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.95rem',
                            textAlign: 'right',
                            fontWeight: '700',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {(totals.liabilities + totals.equity).toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BalanceSheet;
